import { useMemo } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import { EntityType } from '@polyai/stream/store/reducers/entities/types';
import Text from '@polyai/ui/components/atoms/Text';
import Toggle from '@polyai/ui/components/atoms/Toggle';
import type { OptionsType } from '@polyai/ui/components/molecules/Filter/Filter.constants';

import { FormSelect } from 'components/molecules/Form';
import * as Styled from '../EntitySidebar.styled';
import {
  type EntityFormProps,
  type EntitySchemaProps,
  TIME_CONFIG_OPTIONS,
} from '.';

interface Props {
  readonly: boolean;
}

export const TimeForm: React.FC<Props> = ({ readonly }) => {
  const { control, watch, setValue } = useFormContext<EntitySchemaProps>();

  const config = watch('config');
  const startTime = watch('config.value.startTime');

  const isTimeConfig = config?.$case === EntityType.TIME;
  const isRangeEnabled = isTimeConfig && config?.value.enabled;

  // Filter out the selected start time from end time options
  const endTimeOptions = useMemo(() => {
    if (!startTime) {
      return TIME_CONFIG_OPTIONS;
    }
    return TIME_CONFIG_OPTIONS.filter(option => option.value !== startTime);
  }, [startTime]);

  return (
    <>
      <Controller
        control={control}
        name="config.value.enabled"
        render={({ field }) => (
          <Styled.SwitchWrapper>
            <Toggle
              handleToggle={field.onChange}
              readonly={readonly}
              toggleOn={!!field.value}
            />
            <Text>Set time range</Text>
          </Styled.SwitchWrapper>
        )}
      />

      {isRangeEnabled && (
        <Styled.RangeWrapper>
          <Styled.SelectWrapper>
            <FormSelect<EntityFormProps>
              controlKey={'config.value.startTime' as any}
              data-test-id="entity-start-time"
              label="Start time"
              options={TIME_CONFIG_OPTIONS}
              readonly={readonly}
              value={TIME_CONFIG_OPTIONS.find(
                option => option.value === config.value.startTime,
              )}
              onChange={(e: OptionsType | null) => {
                if (!e) {
                  return;
                }
                setValue('config.value.startTime', e.value);

                // Clear end time if it becomes the same as the new start time
                if (config?.value.endTime === e.value) {
                  setValue('config.value.endTime', '');
                }
                // Automatically set end time an hour later if it is not set yet
                else if (!config?.value.endTime) {
                  const [hours, minutes] = e.value.split(':').map(Number);
                  const endHours = (hours + 1) % 24;
                  const endTime = `${endHours
                    .toString()
                    .padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                  setValue('config.value.endTime', endTime);
                }
              }}
            />
          </Styled.SelectWrapper>
          <Styled.SelectWrapper>
            <FormSelect<EntityFormProps>
              controlKey={'config.value.endTime' as any}
              data-test-id="entity-end-time"
              label="End time"
              options={endTimeOptions}
              readonly={readonly}
              value={endTimeOptions.find(
                option => option.value === config.value.endTime,
              )}
              onChange={(e: OptionsType | null) => {
                setValue('config.value.endTime', e?.value ?? '');
              }}
            />
          </Styled.SelectWrapper>
        </Styled.RangeWrapper>
      )}
    </>
  );
};
